import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useTransportationPreference } from 'hooks/business/useTransportationPreference';
import {
  getIndividualBusiness,
  updateIndividualValueForBusinessSettings,
} from 'api/admin/businesses/businesses';
import { updateIndividualValueForStoreSettings } from 'api/admin/stores/stores';
import {
  DELIVERY_PREFERENCES,
  StoreSettings,
  TransportationPreferences,
} from 'constants/store/storeSettings';
import { ApiError } from 'types/api';
import { Business } from 'types/business';
import { Store } from 'types/store';

interface BusinessSettings {
  label: string;
  propertyName?: string;
  description: string;
  dropdown?: boolean;
  input?: boolean;
  options?: Array<{
    display: string;
    value: string;
  }>;
  value?: string;
  onChange?: (value: string) => Promise<unknown>;
}

const BUSINESS_SETTINGS_IN_DB: BusinessSettings[] = [
  {
    label: 'Messaging Enabled',
    propertyName: 'isMessagingEnabled',
    description: 'Enables the Messaging feature for the business.',
  },
  {
    label: 'Assist Enabled',
    propertyName: 'isAssistEnabled',
    description: 'Enables the Assist feature for the business.',
  },
  {
    label: 'Accelerate Enabled',
    propertyName: 'isAccelerateEnabled',
    description:
      'Turning on Accelerate Enabled will expose Blasts + Email Templates + Journeys in Marketing.',
  },
  {
    label: 'Review management',
    propertyName: 'isReviewManagementEnabled',
    description: 'Turning on Review management will expose Reviews in Marketing.',
  },
  {
    label: 'Ads Management',
    propertyName: 'isAdsIntegrationEnabled',
    description: 'Turning on Ads Management will expose Ads in Marketing.',
  },
  {
    label: 'Weight Before Processing',
    propertyName: 'isWeightBeforeProcessing',
    description: 'Requires a weight to be added to service orders when processing begins.',
  },
  {
    label: 'Weight After Processing',
    propertyName: 'isWeightAfterProcessing',
    description: 'Requires a weight to be added to service orders when processing is completed.',
  },
  {
    label: 'Weight Upon Completion',
    propertyName: 'isWeightUpOnCompletion',
    description:
      'Requires a weight to be added to service orders when the order is marked as picked up/completed.',
  },
  {
    label: 'Require Employee Code',
    propertyName: 'requiresEmployeeCode',
    description: 'Requires employee to enter their code for every action taken on Cents.',
  },
  {
    label: 'Enable Online Payments For In-Store Orders',
    propertyName: 'isInStoreOnlinePaymentsEnabled',
    description: 'Allows customers to use online payment methods for orders placed in-store.',
  },
  {
    label: 'Show Customer Preferences in Driver App',
    propertyName: 'isCustomerPreferencesShowingInDriverApp',
    description: 'Allows drivers to see customer preferences in the driver app.',
  },
  {
    label: 'Google Tag Manager ID',
    propertyName: 'gtmId',
    description: 'Google Tag Manager ID',
    input: true,
  },
  {
    label: 'Delivery Timings Preference',
    propertyName: 'deliveryTimingsPreference',
    description: 'Delivery timings preference',
    dropdown: true,
    options: [
      { value: 'OWN_DRIVERS', display: 'Own Drivers' },
      { value: 'ON_DEMAND', display: 'On Demand' },
      { value: 'EARLIEST_AVAILABLE', display: 'Earliest Available' },
    ],
  },
];

// Human-readable label per business-settings property, reused by the Assist migration table.
export const BUSINESS_SETTING_LABELS: Record<string, string> = BUSINESS_SETTINGS_IN_DB.reduce(
  (acc, { propertyName, label }) => {
    if (propertyName) {
      acc[propertyName] = label;
    }

    return acc;
  },
  {} as Record<string, string>,
);

export const useBusinessSettings = ({ id, storeList }: { id: string; storeList: Store[] }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const storeIds = storeList.map(({ id }) => id);

  const [gtmIdValue, setGtmIdValue] = useState<string | null>(null);

  const handleGtmIdChangeDebounced = useCallback(
    debounce((propertyName: string, newValue: string) => {
      saveIndividualValueForBusinessSettings.mutate({
        field: propertyName,
        value: newValue,
      });
    }, 500),
    [],
  );

  const handleGtmIdChange = (propertyName: string, newValue: string) => {
    setGtmIdValue(newValue);
    handleGtmIdChangeDebounced(propertyName, newValue);
  };

  const { transportationPreference, isLoadingTransportationPreference, queryKey } =
    useTransportationPreference({
      storeIds,
      setError,
    });

  const settings: BusinessSettings[] = [
    ...BUSINESS_SETTINGS_IN_DB,
    {
      label: 'Delivery Preference',
      description: 'Use this dropdown to set the operator’s default Delivery Preference.',
      dropdown: true,
      options: DELIVERY_PREFERENCES,
      value: transportationPreference || '',
      onChange: async (value: TransportationPreferences) => {
        await Promise.all(
          storeList.map(({ id }) =>
            updateIndividualValueForStoreSettings(id, {
              field: StoreSettings.TRANSPORTATION_PREFERENCE,
              value,
            }),
          ),
        );
        queryClient.invalidateQueries({
          queryKey,
        });
      },
    },
  ];

  const { data: business, isLoading: isLoadingBusiness } = useQuery<unknown, ApiError, Business>(
    ['retrieveBusiness', { id }],
    async () => {
      const { data } = await getIndividualBusiness(id);
      return data.business;
    },
    {
      onError: (error) => {
        setError(error?.response?.data?.error);
      },
    },
  );

  const saveIndividualValueForBusinessSettings = useMutation<
    unknown,
    ApiError,
    {
      field: string;
      value: string | boolean;
    }
  >({
    mutationFn: ({ field, value }) => {
      return updateIndividualValueForBusinessSettings({
        id,
        field,
        value,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retrieveBusiness', { id }] });
    },
    onError: (error) => {
      setError(error?.response?.data?.error);
    },
  });

  return {
    settings,
    business,
    error,
    isLoadingBusiness,
    isLoadingTransportationPreference,
    saveIndividualValueForBusinessSettings,
    gtmIdValue,
    handleGtmIdChange,
  };
};
